import Section from "../../components/Section";
import {
  FaPhoneAlt,
  FaRobot,
  FaChartLine,
  FaHeadset,
  FaLock,
  FaGlobe,
  FaUsers,
} from "react-icons/fa";
import { aiFeatureImage1 } from "../../assets";
import { aiFeatureImage2 } from "../../assets";
import { aiFeatureImage3 } from "../../assets";
import { aiFeatureImage4 } from "../../assets";
import { aiFeatureImage5 } from "../../assets";
import { aiFeatureImage6 } from "../../assets";
import { aiFeatureImage7 } from "../../assets";

const AiFeature = () => {
  return (
    <Section crosses>
      <div className="container">
        {/* Section Header */}
        <div className="mx-auto text-center">
          <h2 className="h3 p-4 font-extrabold text-white">
            Transform Your Customer Interactions with
            <br /> <span className="text-purple-500">AI Voice Agents</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-400 sm:text-lg">
            Unlock the potential of AI-driven communication to enhance customer
            service, streamline operations, and propel your business towards
            unparalleled growth.
          </p>
        </div>

        {/* Content Grid */}
        <section className="mt-10 grid grid-cols-1 gap-3 md:gap-4 lg:grid-cols-3 lg:gap-8">
          {/* Feature 1 */}
          <div className="grid h-full w-full grid-cols-1 gap-4 rounded-lg border border-gray-800 p-8 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple lg:col-span-2 lg:grid-cols-2">
            <div className="flex flex-col justify-center space-y-3">
              <div className="flex w-fit items-center gap-2">
                <FaPhoneAlt className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Seamless Call Handling
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Our AI Voice Agents ensure efficient call routing during high
                volumes of customer inquiries. With AI, we provide accurate call
                management, reducing wait times and creating smoother
                interactions. This leads to improved customer experience with
                faster resolutions and increased satisfaction.
              </p>
              <ul className="mt-5 list-disc pl-5">
                <li>Efficient routing to handle high call volumes.</li>
                <li>AI-powered call management for seamless accuracy.</li>
                <li>Reduced wait times and enhanced customer experience.</li>
              </ul>
            </div>
            <div className="flex items-center justify-center">
              <img
                src={aiFeatureImage1}
                alt="Seamless Call Handling"
                className="w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 2 */}
          <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-800 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple">
            <div className="p-4 lg:p-8">
              <div className="flex w-fit items-center gap-2">
                <FaRobot className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Intelligent Automation
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Automate repetitive tasks to maximize efficiency. With AI
                handling routine activities, human agents are free to focus on
                more complex and meaningful customer interactions. This not only
                reduces operational costs but also boosts agent productivity and
                customer satisfaction.
              </p>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage2}
                alt="Intelligent Automation"
                className="mt-4 w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 3 */}
          <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-800 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple">
            <div className="p-8">
              <div className="flex w-fit items-center gap-2">
                <FaChartLine className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Real-Time Analytics
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Harness the power of real-time analytics to make data-driven
                decisions. Our AI-powered agents provide advanced insights into
                your customer interactions, allowing you to identify trends,
                resolve issues proactively, and continuously improve your
                service offerings.
              </p>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage3}
                alt="Real-Time Analytics"
                className="mt-4 w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 4 */}
          <div className="grid h-full w-full grid-cols-1 gap-4 rounded-lg border border-gray-800 p-8 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple lg:col-span-2 lg:grid-cols-2">
            <div className="flex flex-col justify-center space-y-3">
              <div className="flex items-center text-xl font-bold text-white md:text-base">
                <FaHeadset className="mr-2 text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  24/7 Availability
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Provide round-the-clock support without worrying about time
                zones. Our AI Voice Agents ensure your business is always
                available, enhancing trust and satisfaction. Rest assured, your
                clients will receive assistance at any hour.
              </p>
              <ul className="mt-5 list-disc pl-5">
                <li>24/7 support with no gaps in service.</li>
                <li>Global reach across multiple time zones.</li>
                <li>
                  AI agents providing constant availability, reducing
                  frustration.
                </li>
              </ul>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage4}
                alt="24/7 Availability"
                className="w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 5 */}
          <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-800 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple">
            <div className="p-8">
              <div className="flex w-fit items-center gap-2">
                <FaLock className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Secure Conversations
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Prioritize customer privacy and data security with end-to-end
                encryption. Our AI Voice Agents ensure all interactions are
                protected, giving your customers confidence in your commitment
                to safeguarding their personal information.
              </p>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage5}
                alt="Secure Conversations"
                className="mt-4 w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 6 */}
          <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-800 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple">
            <div className="p-8">
              <div className="flex w-fit items-center gap-2">
                <FaGlobe className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Global Reach
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Extend your business reach beyond borders. Our AI Voice Agents
                break geographical barriers, providing seamless communication
                with customers around the world. Expand your customer base
                without limitations.
              </p>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage6}
                alt="Global Reach"
                className="mt-4 w-full rounded-md"
              />
            </div>
          </div>

          {/* Feature 7 */}
          <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-800 transition duration-300 hover:bg-slate-900 hover:shadow-xl hover:neon-purple">
            <div className="p-8">
              <div className="flex w-fit items-center gap-2">
                <FaUsers className="mr-2 w-fit text-blue-400" />
                <span className="f-a flex items-center text-lg font-semibold tracking-[0.075em] text-white lg:text-lg">
                  Enhanced Customer Engagement
                </span>
              </div>
              <p className="mt-2 text-slate-300">
                Engage customers on a deeper level with personalized
                communication. Our AI Voice Agents use advanced algorithms to
                remember and respond to customer preferences, enhancing the
                customer journey at every touchpoint.
              </p>
            </div>
            <div className="flex items-center justify-start">
              <img
                src={aiFeatureImage7}
                alt="Enhanced Customer Engagement"
                className="mt-4 w-full rounded-md"
              />
            </div>
          </div>
        </section>
      </div>
    </Section>
  );
};

export default AiFeature;
